import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

export type ButtonColor = "primary" | "secondary" | "white" | "neutral" | "danger";
export type ButtonVariant = "filled" | "outlined" | "ghost";
export type ButtonBreakpoint = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const BREAKPOINTS: ButtonBreakpoint[] = ["xxs","xs", "sm", "md", "lg", "xl", "2xl"];

/** Space between the button and its tooltip, and the minimum margin to the viewport edges. */
const TOOLTIP_GAP_PX = 8;

const LABEL_VISIBILITY_CLASSES: Record<
  ButtonBreakpoint,
  { show: string; hide: string }
> = {
  xxs: { show: "xxs:not-sr-only", hide: "xxs:sr-only" },
  xs: { show: "xs:not-sr-only", hide: "xs:sr-only" },
  sm: { show: "sm:not-sr-only", hide: "sm:sr-only" },
  md: { show: "md:not-sr-only", hide: "md:sr-only" },
  lg: { show: "lg:not-sr-only", hide: "lg:sr-only" },
  xl: { show: "xl:not-sr-only", hide: "xl:sr-only" },
  "2xl": { show: "2xl:not-sr-only", hide: "2xl:sr-only" },
};

export interface IButton {
  text?: string;
  onClick: () => void;
  color: ButtonColor;
  variant: ButtonVariant;
  size: "fixed" | "fluid";
  tooltip?: string;
  disabled?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  className?: string;
  textClassName?: string;
  labelNonVisible?: ButtonBreakpoint[];
}

const buttonStyles: Record<ButtonVariant, Record<ButtonColor, string>> = {
  filled: {
    primary: "bg-primary text-white hover:bg-primary-dark",
    secondary: "bg-secondary text-white hover:bg-accent-Default-dark",
    white: "bg-white text-gray-font hover:bg-gray-100",
    neutral: "bg-gray-800 text-white hover:bg-neutral-dark",
    danger: "bg-red-600 text-white hover:bg-red-700",
  },
  outlined: {
    primary: "outline outline-1 outline-primary text-primary hover:bg-primary-light",
    secondary: "outline outline-1 outline-secondary text-secondary hover:bg-accent-Default-dark hover:text-white",
    white: "outline outline-1 outline-white text-white hover:bg-white/10",
    neutral: "outline outline-1 outline-gray-800 text-gray-800 hover:bg-neutral-dark hover:text-white",
    danger: "outline outline-1 outline-red-600 text-red-600 hover:bg-red-600 hover:text-white",
  },
  ghost: {
    primary: "text-primary hover:bg-primary-light",
    secondary: "text-secondary hover:bg-accent-Default-dark hover:text-white",
    white: "text-white hover:bg-white/10",
    neutral: "text-gray-800 hover:bg-neutral-dark hover:text-white",
    danger: "text-red-600 hover:bg-red-50",
  },
};

interface TooltipPlacement {
  left: number;
  top: number;
  arrowLeft: number;
  above: boolean;
}

/**
 * Rendered in a portal with `position: fixed` because buttons often live inside scrollable
 * containers (table cells, panels): as a descendant, the tooltip is clipped by their overflow
 * and counts as scrollable content, which widens the container's scroll area on hover.
 */
function ButtonTooltip({
  anchorRef,
  text,
}: {
  anchorRef: RefObject<HTMLElement>;
  text: string;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<TooltipPlacement | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const { width, height } = tooltip.getBoundingClientRect();
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const above = anchorRect.top - height - TOOLTIP_GAP_PX >= 0;
    // Keep it inside the viewport: `whitespace-nowrap` makes it wider than the button.
    const maxLeft = Math.max(window.innerWidth - width - TOOLTIP_GAP_PX, TOOLTIP_GAP_PX);
    const left = Math.min(Math.max(anchorCenter - width / 2, TOOLTIP_GAP_PX), maxLeft);

    setPlacement({
      left,
      top: above
        ? anchorRect.top - height - TOOLTIP_GAP_PX
        : anchorRect.bottom + TOOLTIP_GAP_PX,
      // The arrow follows the button even when the tooltip was pushed away from its center.
      arrowLeft: anchorCenter - left,
      above,
    });
  }, [anchorRef]);

  useLayoutEffect(place, [place, text]);

  useEffect(() => {
    // Capture phase: what scrolls is usually an inner container, not the window.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [place]);

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className="pointer-events-none fixed left-0 top-0 z-[100]"
      style={
        placement
          ? { left: placement.left, top: placement.top }
          : // Measured before it is placed, so it must not flash at the wrong spot.
            { visibility: "hidden" }
      }
    >
      <div className="relative bg-gray-font text-neutral text-sm md:text-base px-3 py-2 rounded shadow-lg whitespace-nowrap">
        {text}
        <div
          className={`absolute -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-transparent ${
            placement?.above === false
              ? "bottom-full border-b-4 border-b-gray-font"
              : "top-full border-t-4 border-t-gray-font"
          }`}
          style={{ left: placement?.arrowLeft }}
          aria-hidden
        />
      </div>
    </div>,
    document.body
  );
}

export default function Button({
  text,
  onClick,
  color,
  variant,
  size,
  disabled = false,
  icon,
  tooltip,
  iconPosition = "right",
  className = "",
  textClassName = "",
  labelNonVisible,
}: IButton) {
  const hasText = Boolean(text?.trim());
  const iconOnly = Boolean(icon) && !hasText;
  const sizeClass = size === "fluid" ? "w-full" : " shrink-0 w-full md:w-auto";
  const flexDirection =
    icon && iconPosition === "left" ? "flex-row-reverse" : "flex-row";
  const hiddenBreakpoints = new Set(labelNonVisible ?? []);
  const responsiveLabelClass = icon && hasText
    ? BREAKPOINTS
      .map((breakpoint) =>
        hiddenBreakpoints.has(breakpoint)
          ? LABEL_VISIBILITY_CLASSES[breakpoint].hide
          : LABEL_VISIBILITY_CLASSES[breakpoint].show
      )
      .join(" ")
    : "";

  const anchorRef = useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const buttonEl = (
    <button
      type="button"
      className={`${buttonStyles[variant][color]} ${sizeClass} inline-flex items-center justify-center ${iconOnly ? "p-3" : "gap-2 px-5 py-[10px]"} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${icon && hasText ? flexDirection : ""} ${
        // A disabled button swallows pointer events, so the wrapper would never learn about
        // the hover — and a disabled button is exactly the one whose tooltip explains why.
        tooltip && disabled ? "pointer-events-none" : ""
      } ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && hasText ? (
        <>
          <span
            className={`font-medium ${responsiveLabelClass} ${textClassName}`.trim()}
          >
            {text}
          </span>
          <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:shrink-0 `}>
            {icon}
          </span>
        </>
      ) : iconOnly ? (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:shrink-0">
          {icon}
        </span>
      ) : (
        text
      )}
    </button>
  );

  if (tooltip) {
    return (
      <div
        ref={anchorRef}
        className={`relative inline-block h-full max-w-full ${
          disabled ? "cursor-not-allowed" : ""
        }`.trim()}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
        onFocus={() => setTooltipVisible(true)}
        onBlur={() => setTooltipVisible(false)}
      >
        {buttonEl}
        {tooltipVisible && <ButtonTooltip anchorRef={anchorRef} text={tooltip} />}
      </div>
    );
  }

  return buttonEl;
}
